import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { TemperatureBadge, slugToLabel } from "@/components/common";
import {
  ArrowLeft, Loader2, Calendar, Clock, UserCheck, Video, Plus, Search, Bot,
  CheckCircle2, XCircle, CircleDot, CalendarClock, Ban, Users, Radio, Percent,
} from "lucide-react";

const CONF_STYLE = {
  CONFIRMED: "text-emerald-400 border-emerald-500/30",
  DECLINED: "text-red-400 border-red-500/30",
  WAITING_CONFIRMATION: "text-amber-400 border-amber-500/30",
};
const ATT_STYLE = {
  ATTENDED: "text-emerald-400 border-emerald-500/30",
  NO_SHOW: "text-red-400 border-red-500/30",
  NOT_MARKED: "text-slate-400 border-slate-600/40",
};

export default function WebinarDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("registrations");
  const [addOpen, setAddOpen] = useState(false);
  const [reminderOpen, setReminderOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [rdate, setRdate] = useState({ date: "", start_time: "" });
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState({});
  const [selReg, setSelReg] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/webinars/${id}`); setD(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); navigate("/admin/webinars"); }
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);

  const searchLeads = async () => {
    try {
      const { data } = await api.get("/leads", { params: { q: q || undefined, page_size: 25 } });
      setResults(data.items);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addLeads = async () => {
    const lead_ids = Object.keys(picked).filter((k) => picked[k]);
    if (!lead_ids.length) return toast.error("Select at least one lead");
    setBusy(true);
    try {
      const { data } = await api.post(`/webinars/${id}/leads`, { lead_ids, source: "manual" });
      toast.success(`Added ${data.added}, skipped ${data.skipped}`);
      setAddOpen(false); setPicked({}); setResults([]); setQ(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const updateReg = async (rid, patch) => {
    try { await api.patch(`/webinars/registrations/${rid}`, patch); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const bulkAttendance = async (attendance) => {
    const registration_ids = Object.keys(selReg).filter((k) => selReg[k]);
    if (!registration_ids.length) return toast.error("Select registrations first");
    try {
      const { data } = await api.post("/webinars/registrations/bulk-attendance", { registration_ids, attendance });
      toast.success(`${data.updated} marked ${slugToLabel(attendance)}`); setSelReg({}); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sendReminders = async (target) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/webinars/${id}/reminders`, { target });
      toast.success(`${data.queued} AI reminders queued · ${data.skipped} skipped (DNC/invalid)`);
      setReminderOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const createFollowups = async () => {
    const registration_ids = Object.keys(selReg).filter((k) => selReg[k]);
    if (!registration_ids.length) return toast.error("Select leads to follow up");
    setBusy(true);
    try {
      const { data } = await api.post(`/webinars/${id}/post-followups`, { registration_ids });
      toast.success(`${data.created} follow-ups created for lead owners`); setSelReg({}); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const setStatus = async (status) => {
    try { await api.patch(`/webinars/${id}`, { status }); toast.success(`Webinar ${slugToLabel(status)}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doReschedule = async () => {
    if (!rdate.date) return toast.error("Pick a new date");
    try { await api.patch(`/webinars/${id}`, rdate); toast.success("Webinar rescheduled"); setRescheduleOpen(false); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!d) return <div className="flex justify-center py-24"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>;
  const { webinar: w, registrations: regs, stats } = d;
  const attended = regs.filter((r) => r.attendance === "ATTENDED");
  const noShow = regs.filter((r) => r.attendance === "NO_SHOW");

  const S = ({ label, value, icon: Icon, accent = "text-primary", testid }) => (
    <div data-testid={testid} className="glass rounded-xl p-4">
      <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>{Icon && <Icon className={`h-4 w-4 ${accent}`} />}</div>
      <p className="mt-2 text-2xl font-bold font-heading font-mono">{value ?? 0}</p>
    </div>
  );

  const RegRow = ({ r, mode }) => (
    <tr className="border-b border-slate-800/60 hover:bg-slate-800/30">
      {mode !== "view" && (
        <td className="px-3 py-2.5"><Checkbox checked={!!selReg[r.id]} onCheckedChange={(v) => setSelReg({ ...selReg, [r.id]: v })} data-testid={`sel-reg-${r.id}`} /></td>
      )}
      <td className="px-3 py-2.5">
        <div className="font-medium text-sm">{r.lead?.name || "—"}</div>
        <div className="text-xs text-muted-foreground font-mono">{r.lead?.lead_code} · {r.lead?.mobile}</div>
      </td>
      <td className="px-3 py-2.5 hidden md:table-cell"><TemperatureBadge temp={r.lead?.lead_temperature} /></td>
      <td className="px-3 py-2.5 hidden lg:table-cell text-xs text-muted-foreground">{r.lead?.assigned_employee_name || "Unassigned"}</td>
      <td className="px-3 py-2.5"><span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold ${CONF_STYLE[r.confirmation]}`}>{slugToLabel(r.confirmation)}</span></td>
      <td className="px-3 py-2.5"><span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold ${ATT_STYLE[r.attendance]}`}>{slugToLabel(r.attendance)}</span></td>
      {mode === "registrations" && (
        <td className="px-3 py-2.5">
          <div className="flex gap-1.5">
            <button onClick={() => updateReg(r.id, { confirmation: "CONFIRMED" })} data-testid={`confirm-${r.id}`} title="Confirm" className="p-1.5 rounded-lg border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"><CheckCircle2 className="h-3.5 w-3.5" /></button>
            <button onClick={() => updateReg(r.id, { confirmation: "DECLINED" })} data-testid={`decline-${r.id}`} title="Decline" className="p-1.5 rounded-lg border border-red-500/30 text-red-400 hover:bg-red-500/10"><XCircle className="h-3.5 w-3.5" /></button>
          </div>
        </td>
      )}
      {mode === "attendance" && (
        <td className="px-3 py-2.5">
          <div className="flex gap-1.5">
            <button onClick={() => updateReg(r.id, { attendance: "ATTENDED" })} data-testid={`attended-${r.id}`} title="Attended" className="p-1.5 rounded-lg border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"><CheckCircle2 className="h-3.5 w-3.5" /></button>
            <button onClick={() => updateReg(r.id, { attendance: "NO_SHOW" })} data-testid={`noshow-${r.id}`} title="No-show" className="p-1.5 rounded-lg border border-red-500/30 text-red-400 hover:bg-red-500/10"><Ban className="h-3.5 w-3.5" /></button>
          </div>
        </td>
      )}
    </tr>
  );

  return (
    <div className="space-y-6 pb-10" data-testid="webinar-detail-page">
      <button onClick={() => navigate("/admin/webinars")} className="flex items-center gap-1.5 text-sm text-muted-foreground" data-testid="back-webinars"><ArrowLeft className="h-4 w-4" /> Webinars</button>

      {/* Header */}
      <div className="glass rounded-2xl p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-bold font-heading flex items-center gap-2"><Video className="h-5 w-5 text-primary" />{w.name}</h1>
              <span className="text-[10px] px-2 py-0.5 rounded-full border font-semibold text-blue-400 border-blue-500/30">{w.status}</span>
            </div>
            {w.description && <p className="text-sm text-muted-foreground mt-1 max-w-2xl">{w.description}</p>}
            <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground flex-wrap">
              <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{w.date}</span>
              {w.start_time && <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{w.start_time}{w.end_time ? `–${w.end_time}` : ""}</span>}
              {w.host && <span className="flex items-center gap-1"><UserCheck className="h-3.5 w-3.5" />{w.host}</span>}
              {w.join_url && <a href={w.join_url} target="_blank" rel="noreferrer" className="text-primary hover:underline">Join link</a>}
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant="outline" onClick={() => setReminderOpen(true)} data-testid="send-reminder-button" className="border-slate-700"><Bot className="h-4 w-4 mr-1.5 text-primary" />Send AI Reminder</Button>
            {w.status === "SCHEDULED" && <Button size="sm" variant="outline" onClick={() => setRescheduleOpen(true)} data-testid="reschedule-button" className="border-slate-700"><CalendarClock className="h-4 w-4 mr-1.5" />Reschedule</Button>}
            {w.status === "SCHEDULED" && <Button size="sm" variant="outline" onClick={() => setStatus("COMPLETED")} data-testid="complete-button" className="border-emerald-500/30 text-emerald-400"><CheckCircle2 className="h-4 w-4 mr-1.5" />Mark Complete</Button>}
            {w.status !== "CANCELLED" && <Button size="sm" variant="outline" onClick={() => setCancelOpen(true)} data-testid="cancel-button" className="border-red-500/30 text-red-400"><Ban className="h-4 w-4 mr-1.5" />Cancel</Button>}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <S label="Registered" value={stats.total} icon={Users} accent="text-purple-400" testid="stat-total" />
        <S label="Confirmed" value={stats.confirmed} icon={CheckCircle2} accent="text-emerald-400" testid="stat-confirmed" />
        <S label="Declined" value={stats.declined} icon={XCircle} accent="text-red-400" testid="stat-declined" />
        <S label="Waiting" value={stats.waiting} icon={CircleDot} accent="text-amber-400" testid="stat-waiting" />
        <S label="Attended" value={stats.attended} icon={Radio} accent="text-primary" testid="stat-attended" />
        <S label="No-show" value={stats.no_show} icon={Ban} accent="text-red-400" testid="stat-noshow" />
        <S label="Reminders" value={stats.reminders_sent} icon={Bot} accent="text-blue-400" testid="stat-reminders" />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-900/60 border border-slate-800">
          <TabsTrigger value="registrations" data-testid="tab-registrations">Registrations</TabsTrigger>
          <TabsTrigger value="attendance" data-testid="tab-attendance">Attendance</TabsTrigger>
          <TabsTrigger value="post" data-testid="tab-post">Post-Webinar</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="tab-analytics">Analytics</TabsTrigger>
        </TabsList>

        {/* Registrations */}
        <TabsContent value="registrations" className="mt-4">
          <div className="flex justify-between items-center mb-3">
            <p className="text-sm text-muted-foreground">Manage lead registrations & confirmations.</p>
            <Button size="sm" onClick={() => setAddOpen(true)} data-testid="add-leads-button" className="bg-primary text-primary-foreground"><Plus className="h-4 w-4 mr-1.5" />Add Leads</Button>
          </div>
          <RegTable regs={regs} mode="registrations" selReg={selReg} setSelReg={setSelReg} RegRow={RegRow} withSelect />
        </TabsContent>

        {/* Attendance */}
        <TabsContent value="attendance" className="mt-4">
          <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
            <p className="text-sm text-muted-foreground">Select and bulk-mark attendance.</p>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => bulkAttendance("ATTENDED")} data-testid="bulk-attended" className="border-emerald-500/30 text-emerald-400">Mark Attended</Button>
              <Button size="sm" variant="outline" onClick={() => bulkAttendance("NO_SHOW")} data-testid="bulk-noshow" className="border-red-500/30 text-red-400">Mark No-show</Button>
            </div>
          </div>
          <RegTable regs={regs} mode="attendance" selReg={selReg} setSelReg={setSelReg} RegRow={RegRow} withSelect />
        </TabsContent>

        {/* Post-Webinar */}
        <TabsContent value="post" className="mt-4 space-y-4">
          <div className="flex justify-between items-center flex-wrap gap-2">
            <p className="text-sm text-muted-foreground">Create follow-ups for attendees & re-engage no-shows. Assigned to each lead's owner.</p>
            <Button size="sm" onClick={createFollowups} disabled={busy} data-testid="create-followups-button" className="bg-primary text-primary-foreground"><CalendarClock className="h-4 w-4 mr-1.5" />Create Follow-ups</Button>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-emerald-400 mb-2 flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" />Attended ({attended.length})</h3>
            <RegTable regs={attended} mode="post" selReg={selReg} setSelReg={setSelReg} RegRow={RegRow} withSelect empty="No attendees marked yet." />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-red-400 mb-2 flex items-center gap-1.5"><Ban className="h-4 w-4" />No-shows ({noShow.length})</h3>
            <RegTable regs={noShow} mode="post" selReg={selReg} setSelReg={setSelReg} RegRow={RegRow} withSelect empty="No no-shows marked yet." />
          </div>
        </TabsContent>

        {/* Analytics */}
        <TabsContent value="analytics" className="mt-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="glass rounded-xl p-6">
              <h3 className="text-sm font-semibold mb-4 flex items-center gap-1.5"><Percent className="h-4 w-4 text-primary" />Conversion Funnel</h3>
              {[["Registered", stats.total, "bg-purple-500"], ["Confirmed", stats.confirmed, "bg-blue-500"], ["Attended", stats.attended, "bg-emerald-500"]].map(([label, val, color]) => {
                const pct = stats.total ? Math.round((val / stats.total) * 100) : 0;
                return (
                  <div key={label} className="mb-3">
                    <div className="flex justify-between text-xs mb-1"><span className="text-muted-foreground">{label}</span><span className="font-mono">{val} ({pct}%)</span></div>
                    <div className="h-2 rounded-full bg-slate-800 overflow-hidden"><div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${pct}%` }} /></div>
                  </div>
                );
              })}
            </div>
            <div className="glass rounded-xl p-6 space-y-3">
              <h3 className="text-sm font-semibold mb-2">Key Metrics</h3>
              {[["Attendance Rate", stats.attendance_rate != null ? `${stats.attendance_rate}%` : "—"],
                ["Confirmation Rate", stats.total ? `${Math.round((stats.confirmed / stats.total) * 100)}%` : "—"],
                ["No-show Rate", stats.total ? `${Math.round((stats.no_show / stats.total) * 100)}%` : "—"],
                ["Declined", stats.declined], ["Awaiting Confirmation", stats.waiting],
                ["AI Reminders Sent", stats.reminders_sent]].map(([k, v]) => (
                <div key={k} className="flex justify-between items-center py-2 border-b border-slate-800/60 text-sm"><span className="text-muted-foreground">{k}</span><span className="font-mono font-semibold">{v}</span></div>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Add leads dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-slate-950 border-slate-800 max-w-xl">
          <DialogHeader><DialogTitle>Add Leads to Webinar</DialogTitle></DialogHeader>
          <div className="flex gap-2">
            <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && searchLeads()} placeholder="Search by name, mobile, code…" data-testid="lead-search-input" className="bg-slate-900/60 border-slate-700" />
            <Button onClick={searchLeads} data-testid="lead-search-button" variant="outline" className="border-slate-700"><Search className="h-4 w-4" /></Button>
          </div>
          <div className="max-h-[50vh] overflow-auto space-y-1.5 mt-2">
            {results.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">Search to find leads. Tip: filter to "Webinar Ready" leads in the Leads page.</p>}
            {results.map((l) => (
              <label key={l.id} data-testid={`pick-lead-${l.id}`} className="flex items-center gap-3 rounded-lg border border-slate-800 bg-slate-900/40 p-2.5 cursor-pointer hover:border-slate-700">
                <Checkbox checked={!!picked[l.id]} onCheckedChange={(v) => setPicked({ ...picked, [l.id]: v })} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{l.name} <span className="text-xs text-muted-foreground font-mono">{l.lead_code}</span></div>
                  <div className="text-xs text-muted-foreground">{l.mobile} · {slugToLabel(l.current_status)}</div>
                </div>
                <TemperatureBadge temp={l.lead_temperature} />
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button onClick={addLeads} disabled={busy} data-testid="confirm-add-leads" className="bg-primary text-primary-foreground">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Add ${Object.values(picked).filter(Boolean).length || ""} Leads`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reminder dialog */}
      <Dialog open={reminderOpen} onOpenChange={setReminderOpen}>
        <DialogContent className="bg-slate-950 border-slate-800 max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Bot className="h-5 w-5 text-primary" />Send AI Reminders</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Queues AI outbound reminder calls via the existing AI engine. DNC leads are automatically excluded.</p>
          <div className="space-y-2 mt-2">
            <Button onClick={() => sendReminders("pending")} disabled={busy} data-testid="remind-pending" className="w-full bg-primary text-primary-foreground justify-start">Remind all (except declined)</Button>
            <Button onClick={() => sendReminders("confirmed")} disabled={busy} data-testid="remind-confirmed" variant="outline" className="w-full border-slate-700 justify-start">Remind confirmed only</Button>
            <Button onClick={() => sendReminders("all")} disabled={busy} data-testid="remind-all" variant="outline" className="w-full border-slate-700 justify-start">Remind everyone</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reschedule dialog */}
      <Dialog open={rescheduleOpen} onOpenChange={setRescheduleOpen}>
        <DialogContent className="bg-slate-950 border-slate-800 max-w-md">
          <DialogHeader><DialogTitle>Reschedule Webinar</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-muted-foreground">New date</Label><Input type="date" value={rdate.date} onChange={(e) => setRdate({ ...rdate, date: e.target.value })} data-testid="reschedule-date" className="mt-1 bg-slate-900/60 border-slate-700" /></div>
            <div><Label className="text-xs text-muted-foreground">Start time</Label><Input type="time" value={rdate.start_time} onChange={(e) => setRdate({ ...rdate, start_time: e.target.value })} className="mt-1 bg-slate-900/60 border-slate-700" /></div>
          </div>
          <DialogFooter><Button onClick={doReschedule} data-testid="confirm-reschedule" className="bg-primary text-primary-foreground">Reschedule</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel confirm */}
      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent className="bg-slate-950 border-slate-800">
          <AlertDialogHeader><AlertDialogTitle>Cancel this webinar?</AlertDialogTitle>
            <AlertDialogDescription>All queued AI reminders for this webinar will be stopped. This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-slate-700">Keep</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setStatus("CANCELLED"); setCancelOpen(false); }} data-testid="confirm-cancel" className="bg-red-600 hover:bg-red-700">Cancel Webinar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function RegTable({ regs, mode, RegRow, withSelect, empty = "No registrations yet. Add leads to get started." }) {
  return (
    <div className="glass rounded-xl overflow-x-auto">
      {regs.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground text-sm">{empty}</div>
      ) : (
        <table className="w-full text-left">
          <thead className="text-[11px] uppercase tracking-wider text-muted-foreground border-b border-slate-800">
            <tr>
              {withSelect && <th className="px-3 py-2.5 w-8"></th>}
              <th className="px-3 py-2.5">Lead</th>
              <th className="px-3 py-2.5 hidden md:table-cell">Temp</th>
              <th className="px-3 py-2.5 hidden lg:table-cell">Owner</th>
              <th className="px-3 py-2.5">Confirmation</th>
              <th className="px-3 py-2.5">Attendance</th>
              {(mode === "registrations" || mode === "attendance") && <th className="px-3 py-2.5">Actions</th>}
            </tr>
          </thead>
          <tbody>{regs.map((r) => <RegRow key={r.id} r={r} mode={mode} />)}</tbody>
        </table>
      )}
    </div>
  );
}
